import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Rating,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import calendlyAssessmentStyle from "./calendlyAssessmentStyle";
import ValidatorSelect from "../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import useForm, { UseFormProps } from "../../hooks/useForm";
import { requiredFieldDefault } from "../../services/validationRules";
import { convertBooleanToYesNoString, formValue } from "../../services/formServiceFunctions";
import ButtonLoading from "../Button/ButtonLoading";
import ValidatorTextField from "../Validator/ValidatorTextField";
import _ from "lodash";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import { getCalendlyEventReference, getCalendlyPhoneCallIds, getUserConsent, hasLegacyUserConsent, hasUserConsent } from "../../services/calendlyService";
import { useSnackbar } from "notistack";
import { userHasOneOfTheseRoles, userHasRole } from "../../services/backofficeUserService";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { BackofficeUser, Roles } from "../../types/BackofficeUser";
import { createNoteAsFile } from "../../services/Files/fileService";
import ValidatorElectronicFileFolderPathSelect from "../Validator/ValidatorElectronicFileFolderPathSelect";
import { Activity } from "../../types/Activity";
import { CalendlyEvent } from "../../types/Calendly/CalendlyEvent";
import { NotePdfGenerateData } from "../../types/NotePdfGenerateData";
import { apiGet, apiPost } from "../../services/Api/apiCall";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import { useCurrentEditor } from "@tiptap/react";
import { getEditorHtml, setEditorContent } from "../../services/Editor/editorFunctions";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LabeledFieldset from "../LabeledFieldset/LabeledFieldset";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { isFlexServiceProduct } from "../../services/advocardFlexServiceService";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";
import { PhoneCallSummaryResponse } from "../../types/PhoneCall";

type CalendlyAssessmentModalInnerProps = {
  activity: Activity;
  product: ProductUnion;
  update: Function;
  open: boolean;
  handleClose: Function;
  calendlyEvent: CalendlyEvent;
};

const CalendlyAssessmentModalInner = ({
  activity,
  product,
  update,
  open,
  handleClose,
  calendlyEvent,
}: CalendlyAssessmentModalInnerProps) => {
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const currentUser = useCurrentUser();
  const { editor } = useCurrentEditor();
  const { enqueueSnackbar } = useSnackbar();
  const isLawyerCounselingCall = activity.type === "lawyer_counseling_call";
  const isExternalCall = activity.type === "calendly_event";
  const [isReprocessing, setIsReprocessing] = useState<boolean>(false);
  const [selectedPhoneCallId, setSelectedPhoneCallId] = useState<number | null>(null);
  const phoneCallIds = getCalendlyPhoneCallIds(calendlyEvent);
  const phoneCallId = selectedPhoneCallId !== null && phoneCallIds.includes(selectedPhoneCallId)
    ? selectedPhoneCallId : (phoneCallIds.at(-1) ?? null);
  const offerMandateToExternalLawyer =
    !isLawyerCounselingCall &&
    userHasRole(currentUser, Roles.external) &&
    ["death", "alimony", "familyLawCase"].includes(product.productClassName);

  const mediaObjectsFilter = {
    product: product.productClassName,
    productId: product.id,
  };

  const initialValues = {
    cancelled: false,
    processed: convertBooleanToYesNoString(calendlyEvent.assessment!.processed),
    notProcessedReason: formValue(activity, "calendlyEvent.assessment.notProcessedReason"),
    followUp: convertBooleanToYesNoString(calendlyEvent.assessment!.followUp),
    customerSatisfaction: calendlyEvent.assessment!.customerSatisfaction,
    note: formValue(activity, "calendlyEvent.assessment.note"),
    saveNoteAsFile: false,
    dontSendSummaryToClient: false,
    electronicFileFolderPath: "none",
    acceptsMandateTransferal: convertBooleanToYesNoString(calendlyEvent.assessment!.acceptsMandateTransferal),
    callSummary: null,
    cancelReason: formValue(
      activity,
      "calendlyEvent.assessment.cancelReason",
      "Leider müssen wir Ihnen den Beratungstermin krankheitsbedingt absagen. " +
        "Wir bitten die dadurch entstandenen Umstände zu entschuldigen! " +
        "Bitte buchen Sie sich über den Ihnen bekannten Link einen neuen Beratungstermin. " +
        "Mit freundlichen Grüßen, Ihr Team von Legalbird"
    ),
  };

  const queryClient = useQueryClient();

  // TODO: remove after a grace period and when consent field are removed from calendly event
  const hasRecordingAndSummaryConsent = getUserConsent(product.customer, "callRecordingAndSummary") !== null
    ? hasUserConsent(product.customer, "callRecordingAndSummary")
    : hasLegacyUserConsent(calendlyEvent, "callRecordingAndSummary");

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    const updateData = _.merge(values, {
      processed: values.processed === "yes",
      followUp: isLawyerCounselingCall ? null : values.followUp === "yes",
      customerSatisfaction: isLawyerCounselingCall ? null : parseInt(values.customerSatisfaction),
      acceptsMandateTransferal:
        !offerMandateToExternalLawyer || values.followUp === "no" ? null : values.acceptsMandateTransferal === "yes",
      cancelReason: !values.cancelled ? null : values.cancelReason,
      callSummary: editor?.isEmpty ? null : getEditorHtml(editor),
    });

    await updateMutation.mutateAsync({
      id: calendlyEvent.assessment!.id!,
      uri: "calendly_assessments",
      data: updateData,
    });
    await update();

    if (values.saveNoteAsFile) {
      let data: NotePdfGenerateData = {
        ...values,
        productId: product.id,
        documentIdentifier: "consultationSavedNote",
        createdBy:
          currentUser.person.gender.replace("female", "RAin").replace("male", "RA") + " " + currentUser.person.fullname,
      };

      let messageText = "Notiz wurde erfolgreich erstellt";
      let snackbarVariant: "custom" | "error" = "custom";
      try {
        await createNoteAsFile(data);
      } catch (e) {
        messageText = "Notiz konnte nicht erstellt werden";
        snackbarVariant = "error";
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("media_objects", mediaObjectsFilter)
      });

      enqueueSnackbar(messageText, {
        variant: snackbarVariant,
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    clearForm();
    handleClose();

    if (values.cancelled) {
      enqueueSnackbar("Termin wird über Calendly abgesagt. Dies kann eine kurze Zeit in Anspruch nehmen.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 10000,
      });
    }
  };

  const { values, handleChange, handleSubmit, isLoading, registerValidators, errors, clearForm } = useForm({
    initialValues,
    onSubmit,
    identifier: activity,
  });
  const MAX_RETRIES = 90;
  const summaryQueries = useQueries({
    queries: phoneCallIds.map((id) => ({
      queryKey: queryKeys.item("lb/services/phone_call_summary", id),
      queryFn: (): Promise<PhoneCallSummaryResponse> => fetchResource("lb/services/phone_call_summary", id),
      enabled: open && hasRecordingAndSummaryConsent,
      refetchInterval: (query: { state: { data: PhoneCallSummaryResponse | undefined; dataUpdateCount: number } }) => {
        const data = query.state.data;
        return query.state.dataUpdateCount <= MAX_RETRIES && data?.recordingEnabled &&
          !data.summary && data.processingFailed !== true && data.processingStatus !== "skipped"
          ? 5000 : false;
      },
      refetchIntervalInBackground: false,
    })),
  });
  const selectedQuery = summaryQueries[phoneCallIds.indexOf(phoneCallId ?? 0)];
  const phoneCallSummary = selectedQuery?.data;
  const phoneCallProgress = phoneCallSummary;
  const retryCount = phoneCallId === null ? 0 :
    (queryClient.getQueryState(queryKeys.item("lb/services/phone_call_summary", phoneCallId))?.dataUpdateCount ?? 0);
  const recordingEnabled = Boolean(calendlyEvent.assessment?.callSummary) ||
    summaryQueries.some((query) => query.data?.recordingEnabled);

  const hasSubmitRights = (currentUser: BackofficeUser, activity: Activity, values: Record<string, any>) => {
    return (
      currentUser["@id"] === activity.assignedUser ||
      userHasRole(currentUser, Roles.admin) ||
      (isLawyerCounselingCall && userHasRole(currentUser, Roles.lawyer)) ||
      (isExternalCall && userHasOneOfTheseRoles(currentUser, [Roles.lawyer, Roles.accountmanager])) ||
      (values.cancelled && userHasOneOfTheseRoles(currentUser, [Roles.lawyer, Roles.accountmanager]))
    );
  };

  const isSubmittable = () => {
    if (hasSubmitRights(currentUser, activity, values) === false) {
      return false;
    }

    if (values.cancelled === true) {
      return values.cancelReason.length >= 50;
    }

    if (values.saveNoteAsFile === true && values.electronicFileFolderPath === "none") {
      return false;
    }

    if (values.processed === "no") {
      return true;
    }

    if (recordingEnabled && editor?.isEmpty && values.dontSendSummaryToClient !== true) {
      return false;
    }

    if (isLawyerCounselingCall || isFlexServiceProduct(product)) {
      return !_.isEmpty(values.processed);
    }

    if (offerMandateToExternalLawyer && values.followUp === "yes" && !values.acceptsMandateTransferal) {
      return false;
    }

    return !_.isEmpty(values.processed) && values.customerSatisfaction && !_.isEmpty(values.followUp);
  };

  useEffect(() => {
    registerValidators("processed", !values.cancelled ? requiredFieldDefault : []);
    registerValidators(
      "followUp",
      !values.cancelled && !isLawyerCounselingCall && !isFlexServiceProduct(product) ? requiredFieldDefault : []
    );
    registerValidators(
      "notProcessedReason",
      !values.cancelled && values.processed === "no" ? requiredFieldDefault : []
    );
    if (values.processed === "no") {
      handleChange({ target: { name: "followUp", value: "no" } });
    }
  }, [values.cancelled, values.processed]);

  const tooManyRetries = retryCount > MAX_RETRIES;

  const startReprocessing = async () => {
    if (phoneCallId === null) {
      return;
    }
    setIsReprocessing(true);
    try {
      await apiPost("/twilio/reprocess_failed_call", { phoneCallId });
      await queryClient.resetQueries({
        queryKey: queryKeys.item("lb/services/phone_call_summary", phoneCallId),
      });
    } catch {
      enqueueSnackbar("Die erneute Verarbeitung konnte nicht gestartet werden.", { variant: "error" });
    } finally {
      setIsReprocessing(false);
    }
  };

  const waitingForSummary = Boolean(phoneCallSummary?.recordingEnabled && !phoneCallSummary.summary &&
    phoneCallSummary.processingFailed !== true && phoneCallSummary.processingStatus !== "skipped");
  const showProcessing = isReprocessing || (waitingForSummary && !tooManyRetries);

  if (!editor) {
    return null;
  }

  return (
    <LegalbirdIoModal
      handleClose={handleClose}
      open={open}
      title={"Ergebnis des Beratungstermins"}
      submitButton={
        <ButtonLoading
          type={"submit"}
          variant={"contained"}
          disabled={!isSubmittable()}
          isLoading={isLoading}
          onClick={isSubmittable() ? handleSubmit : () => {}}
        >
          {values.cancelled ? "Termin absagen" : "Ergebnis speichern"}
        </ButtonLoading>
      }
    >
      <Grid container>
        {isFlexServiceProduct(product) && (
          <Alert severity="info">
            Spezialfall: Mandantschaft kann in Servicewelt selbst entscheiden, ob weitere Mandatstätigkeit notwendig ist
          </Alert>
        )}
        {userHasOneOfTheseRoles(currentUser, [Roles.lawyer, Roles.accountmanager, Roles.admin]) &&
          dayjs(activity.due) > dayjs() && (
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="cancelled"
                    checked={values.cancelled}
                    onChange={handleChange}
                    value="cancelled"
                    color="primary"
                  />
                }
                label="Termin absagen"
              />
            </Grid>
          )}
        {!values.cancelled ? (
          <>
            <Grid size={12}>
              <ValidatorSelect
                label={"Hat das Gespräch wie geplant stattgefunden?"}
                name={"processed"}
                value={formValue(values, "processed")}
                onChange={handleChange}
                registerValidators={registerValidators}
                validators={!values.cancelled ? requiredFieldDefault : []}
                dependentValidationFields={["notProcessedReason"]}
                error={!!errors["processed"]}
                helperText={errors["processed"]}
                disabled={activity.done}
              >
                <MenuItem value={"yes"}>Ja</MenuItem>
                <MenuItem value={"no"}>Nein</MenuItem>
              </ValidatorSelect>
            </Grid>
            {values.processed === "no" && (
              <Grid size={12}>
                <ValidatorSelect
                  label={"Warum hat das Gespräch nicht stattgefunden? "}
                  name={"notProcessedReason"}
                  value={formValue(values, "notProcessedReason")}
                  onChange={handleChange}
                  disabled={values.processed !== "no" || activity.done}
                  error={!!errors["notProcessedReason"]}
                  helperText={errors["notProcessedReason"]}
                >
                  <MenuItem value={"clientNotAvailable"}>Mandantschaft nicht erreicht</MenuItem>
                  <MenuItem value={"clientNumberIncorrect"}>Falsche Rufnummer</MenuItem>
                  <MenuItem value={"clientCancelled"}>Mandantschaft hat Termin abgesagt</MenuItem>
                  <MenuItem value={"lawyerUnavailable"}>Ich konnte nicht teilnehmen</MenuItem>
                </ValidatorSelect>
              </Grid>
            )}
            {values.processed === "yes" && !isFlexServiceProduct(product) && (
              <>
                {!isLawyerCounselingCall && (
                  <>
                    <Grid size={12}>
                      <ValidatorSelect
                        label={"Weitere Mandatstätigkeiten notwendig"}
                        name={"followUp"}
                        value={formValue(values, "followUp")}
                        onChange={(e) => {
                          handleChange(e);
                          registerValidators(
                            "note",
                            e.target.value === "yes" && !recordingEnabled ? requiredFieldDefault : [],
                          );
                        }}
                        registerValidators={registerValidators}
                        validators={!values.cancelled && values.processed === "no" ? requiredFieldDefault : []}
                        dependentValidationFields={["note"]}
                        error={!!errors["followUp"]}
                        helperText={errors["followUp"]}
                        disabled={activity.done}
                      >
                        <MenuItem value={"yes"}>Ja, es sind weitere Mandatstätigkeiten notwendig</MenuItem>
                        <MenuItem value={"no"}>Nein, alle Fragen wurden beantwortet</MenuItem>
                      </ValidatorSelect>
                    </Grid>
                    {values.followUp === "yes" && !activity.done && offerMandateToExternalLawyer && (
                      <Grid size={12}>
                        <ValidatorSelect
                          label={"Übernahme des Mandats"}
                          name={"acceptsMandateTransferal"}
                          value={formValue(values, "acceptsMandateTransferal")}
                          onChange={handleChange}
                          error={!!errors["acceptsMandateTransferal"]}
                          helperText={errors["acceptsMandateTransferal"]}
                        >
                          <MenuItem value={"yes"}>Ja, ich kann gerne übernehmen</MenuItem>
                          <MenuItem value={"no"}>Nein, ich kann nicht übernehmen</MenuItem>
                        </ValidatorSelect>
                      </Grid>
                    )}
                  </>
                )}
                {!isLawyerCounselingCall && (
                  <Grid sx={calendlyAssessmentStyle.topSpace} size={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Zufriedenheit der Mandantschaft mit der Beratung</FormLabel>
                      <Box sx={calendlyAssessmentStyle.ratingContainer}>
                        <Rating
                          name={"customerSatisfaction"}
                          value={parseInt(values.customerSatisfaction)}
                          onChange={(e, value) => {
                            const target = e.target as HTMLInputElement;

                            handleChange({ target: { name: target.name, value: value } });
                          }}
                          disabled={activity.done}
                        />
                      </Box>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
            <Grid size={12}>
              <ValidatorTextField
                label={"Zusätzliche Hinweise zum Gespräch oder weiteren Mandatstätigkeiten"}
                multiline
                rows={8}
                name={"note"}
                value={formValue(values, "note")}
                onChange={handleChange}
                error={!!errors["note"]}
                helperText={errors["note"]}
                disabled={activity.done}
                registerValidators={() => {}}
                validators={[]}
                isHighlighted={false}
                initialDependentValidationFields={[]}
              />
            </Grid>
            {recordingEnabled && values.processed === "yes" && (
              <>
                <Grid size={12}>
                  {phoneCallIds.length > 0 && (
                    <TextField select fullWidth label="Gespräch auswählen" value={phoneCallId ?? ""}
                      onChange={(event) => setSelectedPhoneCallId(Number(event.target.value))}>
                      {phoneCallIds.map((id, index) => (
                        <MenuItem key={id} value={id}>
                          Gespräch {index + 1}{summaryQueries[index].data?.created
                            ? ` – ${dayjs(summaryQueries[index].data.created).format("DD.MM.YYYY HH:mm")}` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {selectedQuery?.isError && <Alert severity="error">Gespräch konnte nicht geladen werden.</Alert>}
                  {phoneCallSummary?.summary && (
                    <>
                      <Box sx={{ my: 2, maxHeight: "16rem", overflow: "auto" }}
                        dangerouslySetInnerHTML={{ __html: phoneCallSummary.summary }} />
                      <Button disabled={Boolean(activity.done)} onClick={() => setEditorContent(editor, phoneCallSummary.summary ?? "")}>
                        {editor.isEmpty ? "Zusammenfassung übernehmen" : "Editorinhalt durch diese Zusammenfassung ersetzen"}
                      </Button>
                    </>
                  )}
                  {phoneCallSummary && (!phoneCallSummary.recordingEnabled || phoneCallSummary.processingStatus === "skipped") && (
                    <Typography>Für dieses Gespräch wird keine Zusammenfassung erstellt.</Typography>
                  )}
                  <LabeledFieldset sx={{ mb: "0.5rem", mt: "0.5rem" }}>
                    <legend>Zusammenfassung des Gesprächs</legend>
                    <Box
                      sx={{
                        p: 2,
                        mb: 1,
                      }}
                    >
                      <LegalbirdTipTapEditor
                        editorSx={{ border: "none", padding: 0, maxHeight: "400px", overflowY: "auto" }}
                      />
                    </Box>
                  </LabeledFieldset>
                  {tooManyRetries && !isReprocessing && (
                    <Typography
                      sx={{
                        color: "warning.main",
                      }}
                    >
                      Die Erstellung der Zusammenfassung schein länger zu dauern als gewöhnlich. Bitte versuchen Sie es
                      später noch einmal.
                    </Typography>
                  )}
                  {phoneCallSummary && phoneCallSummary.processingFailed === true && !isReprocessing && (
                    <Typography
                      sx={{
                        color: "warning.main",
                      }}
                    >
                      Bei der Erstellung der Zusammenfassung ist ein Fehler aufgetreten.{" "}
                      <Button variant={"text"} onClick={() => startReprocessing()}>
                        Jetzt Generierung neu starten.
                      </Button>
                    </Typography>
                  )}
                  {showProcessing && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "flex-start",
                      }}
                    >
                      <CircularProgress size={20} color={"primary"} />
                      <Typography sx={{ display: "inline-flex", alignItems: "center", gap: 1 }} color={"primary"}>
                        Die Zusammenfassung des Gesprächs wird gerade generiert.
                        <Tooltip title="Telefonmitschnitt empfangen" arrow enterDelay={300}>
                          <RecordVoiceOverIcon
                            fontSize="small"
                            sx={{ opacity: 0.7 }}
                            color={phoneCallProgress?.hasRecording ? "primary" : "disabled"}
                          />
                        </Tooltip>
                        <Tooltip title="Transkription erstellt" arrow enterDelay={300}>
                          <EditNoteIcon
                            fontSize="small"
                            sx={{ opacity: 0.7 }}
                            color={phoneCallProgress?.hasTranscription ? "primary" : "disabled"}
                          />
                        </Tooltip>
                        <Tooltip title="Zusammenfassung erstellt" arrow enterDelay={300}>
                          <ArticleOutlinedIcon
                            fontSize="small"
                            sx={{ opacity: 0.7 }}
                            color={phoneCallProgress?.hasCallSummary ? "primary" : "disabled"}
                          />
                        </Tooltip>
                      </Typography>
                    </Stack>
                  )}
                  {phoneCallSummary && !isReprocessing && phoneCallSummary.summary !== null && (
                    <Typography>
                      Die ausgewählte Zusammenfassung kann ausdrücklich in den Editor übernommen werden.
                      Bitte den Text vor dem Speichern prüfen und gegebenenfalls anpassen.
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={"dontSendSummaryToClient"}
                        value={formValue(values, "dontSendSummaryToClient")}
                        onChange={(e) => {
                          handleChange(e);
                          registerValidators("callSummary", e.target.value === "true" ? requiredFieldDefault : []);
                        }}
                      />
                    }
                    label={"Mandantschaft keine Zusammenfassung schicken"}
                  />
                </Grid>
              </>
            )}
            {!currentUser.isExternal && (
              <>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={"saveNoteAsFile"}
                        value={formValue(values, "saveNoteAsFile")}
                        onChange={handleChange}
                      />
                    }
                    label={"Telefon-Notiz als Dokument speichern"}
                  />
                </Grid>
                {values.saveNoteAsFile && (
                  <Grid size={12}>
                    <ValidatorElectronicFileFolderPathSelect
                      handleChange={handleChange}
                      values={values}
                      errors={errors}
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Grid size={12}>
              <ValidatorTextField
                label={"Absagegrund für die Mandantschaft"}
                multiline
                rows={8}
                name={"cancelReason"}
                value={formValue(values, "cancelReason")}
                onChange={handleChange}
                helperText={
                  <Box
                    sx={{
                      color: values.cancelReason.length < 50 ? "red" : "#999",
                    }}
                  >
                    Anzahl Zeichen {values.cancelReason.length} von mindestens 50
                  </Box>
                }
                registerValidators={() => {}}
                validators={[]}
                isHighlighted={false}
                initialDependentValidationFields={[]}
              />
            </Grid>
          </>
        )}
      </Grid>
    </LegalbirdIoModal>
  );
};

type CalendlyAssessmentModalProps = {
  activity: Activity;
} & Omit<CalendlyAssessmentModalInnerProps, "calendlyEvent">;

const CalendlyAssessmentModal = (props: CalendlyAssessmentModalProps) => {
  const { activity, open } = props;
  const [calendlyEvent, setCalendlyEvent] = useState<CalendlyEvent | null>(null);

  useEffect(() => {
    const calendlyEventReference = getCalendlyEventReference(activity.calendlyEvent);
    let active = true;
    setCalendlyEvent(null);
    if (open && calendlyEventReference) {
      apiGet<CalendlyEvent>(calendlyEventReference).then((event) => {
        if (active) {
          setCalendlyEvent(event);
        }
      });
    }
    return () => {
      active = false;
    };
  }, [open, activity.calendlyEvent]);

  if (!open || !calendlyEvent) {
    return null;
  }

  return (
    <LegalbirdTipTapEditorProvider key={calendlyEvent.id} placeholder={"Zusammenfassung des Gesprächs"}
      initialContent={calendlyEvent.assessment?.callSummary ?? ""}>
      <CalendlyAssessmentModalInner {...props} calendlyEvent={calendlyEvent} />
    </LegalbirdTipTapEditorProvider>
  );
};

export default CalendlyAssessmentModal;
